import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState, useContext } from "react";
import { createAvatar } from "@dicebear/core";
import { initials } from "@dicebear/collection";
import {
  NavItem,
  NavLink,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { ImHome } from "react-icons/im";
import { GiHamburgerMenu, GiBrain } from "react-icons/gi";
import { AiOutlineVideoCameraAdd } from "react-icons/ai";
import { HiSparkles } from "react-icons/hi";
import {
  RiAdminLine,
  RiPlayList2Fill,
  RiLoginCircleLine,
  RiLogoutCircleRLine,
} from "react-icons/ri";

import Search from "../Search";
import Logo from "../../images/ULogo.png";
import AddNewRecord from "../AddNewRecord";
import PaymentModal from "../PaymentModal/PaymentModal";
import GenerationCounter from "../GenerationCounter/GenerationCounter";
import { AppContext } from "../../store/AppContext";
import { getUserInfo, getCategories, logout } from "../../Services";
import { axiosInstance } from "../../Services/axios";

import "./header.scss";

const Header = (props) => {
  const {
    isLoggedIn,
    setLoggedIn,
    userDetails,
    setUserDetails,
    categories,
    setCategories,
    usageInfo: contextUsageInfo,
    setUsageInfo,
  } = useContext(AppContext);
  const navigate = useNavigate();
  const [activePath, setPath] = useState("/home");
  const [isAddNewOpen, setAddNewOpen] = useState(false);
  const [isPaymentModalOpen, setPaymentModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isToggleOpen, setToggleOpen] = useState(false);

  const url = window.location.pathname;
  const toggle = () => {
    setDropdownOpen((prevState) => !prevState);
  };

  const headerList = [
    {
      name: "BrainSNAP",
      path: "/brain-snap",
      isAdmin: false,
      logo: GiBrain,
    },
    {
      name: "Add New Record",
      path: null,
      isAdmin: false,
      logo: AiOutlineVideoCameraAdd,
    },
    {
      name: "My Videos",
      path: "/videos",
      isAdmin: false,
      logo: RiPlayList2Fill,
    },
    {
      name: "Admin",
      path: "/admin",
      isAdmin: true,
      logo: RiAdminLine,
    },
    {
      name: "Pro",
      path: null,
      isAdmin: false,
      logo: HiSparkles,
    },
    {
      name: "SignOut",
      path: "/",
      isAdmin: false,
      logo: RiLogoutCircleRLine,
    },
  ];

  useEffect(() => {
    if (isLoggedIn && userDetails === null) {
      (async () => {
        let users = await getUserInfo();
        setUserDetails(users);
        if (users?.email) {
          generateAvatar(users.email);
        }
      })();
    } else if (isLoggedIn && userDetails?.email) {
      generateAvatar(userDetails.email);
    }
  }, [isLoggedIn, userDetails]);

  useEffect(() => {
    (async () => {
      try {
        const response = await getCategories();
        if (response && response.categoryNames && Array.isArray(response.categoryNames)) {
          setCategories(response.categoryNames.sort());
        } else {
          setCategories([]);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories([]);
      }
    })();

    window.addEventListener("resize", () => setToggleOpen(false));
    return () => {
      window.removeEventListener("resize", () => setToggleOpen(false));
    };
  }, []);

  useEffect(() => {
    userDetails?.email && generateAvatar(userDetails.email);
  }, [dropdownOpen]);

  const generateAvatar = (email) => {
    let avatar = createAvatar(initials, {
      seed: email,
    });

    var container = document.getElementById("avatar-element");
    var dropdown = document.getElementById("avatar-element-drop-down");
    if (dropdown) dropdown.innerHTML = avatar.toJson().svg;
    if (container) container.innerHTML = avatar.toJson().svg;
  };

  const isPathActive = (path) => {
    return path === activePath;
  };

  const onNavigate = async (path, name) => {
    if (name === "SignOut" && userDetails?.email) {
      const response = await logout(userDetails.email);
      if (response.success) {
        setLoggedIn(false);
        setUserDetails(null);
      }
    }
    setPath(path);
  };

  const onAddNewClick = () => {
    setAddNewOpen(true);
  };

  const onProClick = () => {
    setPaymentModalOpen(true);
  };

  const refreshUsageInfo = async () => {
    try {
      const response = await axiosInstance.get('/brain-snap/stripe/usage-info');
      if (response.data.success) {
        setUsageInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error refreshing usage info:', error);
    }
  };

  const handlePaymentSuccess = async () => {
    setPaymentModalOpen(false);
    setSuccessMessage('Payment successful! Your counter has been updated.');

    // Refresh usage info
    await refreshUsageInfo();

    // Reload page after 2 seconds to show fresh data
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  const onHeaderNavigatorClick = (path, name) => {
    // Always close. Toggling here flipped the collapsed nav open when this ran
    // from the avatar dropdown, switching the header to the expanded navbar
    // layout on the destination page (seen on /admin).
    setToggleOpen(false);
    onNavigate(path, name);
  }

  const getOptions = () => {
    let options = [];

    if (userDetails?.email) {
      options.push(
        <div className="user-details" key="user-details">
          <div id="avatar-element-drop-down" />
          <div className="email">{userDetails.email}</div>
        </div>
      );
    }

    headerList.forEach((item) => {
      if (item.isAdmin && userDetails?.role === "ADMIN") {
        options.push(
          <DropdownItem key={item.name}>
            <Link
              to={item.path}
              onClick={() => onHeaderNavigatorClick(item.path, item.name)}
            >
              <item.logo />
              {item.name}
            </Link>
          </DropdownItem>
        );
      } else {
        if (item.path === null) {
          if (item.name === "Add New Record") {
            options.push(
              <DropdownItem key={item.name}>
                <a onClick={onAddNewClick}>
                  <item.logo />
                  {item.name}
                </a>
              </DropdownItem>
            );
          } else if (item.name === "Pro") {
            options.push(
              <DropdownItem key={item.name}>
                <a onClick={onProClick}>
                  <item.logo />
                  {item.name}
                </a>
              </DropdownItem>
            );
          }
        } else {
          !item.isAdmin && options.push(
            <DropdownItem key={item.name}>
              <Link
                to={item.path}
                onClick={() => onNavigate(item.path, item.name)}
              >
                <item.logo />
                {item.name}
              </Link>
            </DropdownItem>
          );
        }
      }
    });

    return options;
  };

  useEffect(() => {
    setPath(url);
  }, [url]);

  const onLogoClick = () => {
    onNavigate("/home");
    navigate("/home");
  };

  const getNavItems = (isLoggedIn) => {
    if (isLoggedIn) {
      let userOptionList = [];
      headerList.forEach((item) => {
        if (item.isAdmin && userDetails?.role === "ADMIN") {
          userOptionList.push(
            <NavItem key={item.name}>
              <NavLink tag="div" className={item.name === "BrainSNAP" ? "brain-snap-btn" : ""}>
                <Link
                  to={item.path}
                  onClick={() => onNavigate(item.path, item.name)}
                >
                  <item.logo />
                  {item.name}
                </Link>
              </NavLink>
            </NavItem>
          );
        } else {
          if (item.path === null) {
            userOptionList.push(
              <NavItem key={item.name}>
                <NavLink tag="div">
                  <a onClick={onAddNewClick}>
                    <item.logo />
                    {item.name}
                  </a>
                </NavLink>
              </NavItem>
            );
          } else {
            !item.isAdmin && userOptionList.push(
              <NavItem key={item.name}>
                <NavLink tag="div" className={item.name === "BrainSNAP" ? "brain-snap-btn" : ""}>
                  <Link
                    to={item.path}
                    onClick={() => onNavigate(item.path, item.name)}
                  >
                    <item.logo />
                    {item.name}
                  </Link>
                </NavLink>
              </NavItem>
            );
          }
        }
      });

      return (
        <>
          <div className="user-details-minview">
            <div id="avatar-element" />
            <div className="email">{userDetails?.email}</div>
          </div>
          {userOptionList}
        </>
      );
    } else {
      return (
        <>
          <NavItem>
            {isPathActive("/category") ? (
              <NavLink tag="div">
                <Link to="/home">
                  <ImHome />
                  Home
                </Link>
              </NavLink>
            ) : null}
            <NavLink tag="div" className={isPathActive("/login") ? "active" : ""}>
              <Link to="/login">
                <RiLoginCircleLine />
                Login
              </Link>
            </NavLink>
          </NavItem>
        </>
      );
    }
  };

  const onNavToggleClick = () => {
    setTimeout(() => {
      if (userDetails?.email) {
        generateAvatar(userDetails.email);
      }
    }, 0);
    setToggleOpen(!isToggleOpen);
  };

  return (
    <div>
      <nav className="navbar navbar-expand-lg navbar-light bg-light">
        <a className="navbar-brand" onClick={onLogoClick}>
          <img src={Logo} onClick={onLogoClick} />
        </a>
        <Search category={categories} />
        <button
          className="navbar-toggler"
          type="button"
          data-toggle="collapse"
          data-target="#navbarSupportedContent"
          aria-controls="navbarSupportedContent"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="custom-toggle-icon">
            <GiHamburgerMenu onClick={onNavToggleClick} />
          </span>
        </button>
        <div
          class={`collapse navbar-collapse ${isToggleOpen && "show"}`}
          id="navbarSupportedContent"
        >
          {isToggleOpen ? (
            <ul className="navbar-nav mr-auto">{getNavItems(isLoggedIn)}</ul>
          ) : (
            <ul className="navbar-nav">
              {isLoggedIn && (
                <>
                  <NavItem className="generation-counter-item">
                    <GenerationCounter usageInfo={contextUsageInfo} />
                  </NavItem>
                  <NavItem>
                    <NavLink tag="div">
                      <Dropdown
                        className="user-dropdown"
                        isOpen={dropdownOpen}
                        toggle={toggle}
                      >
                        <DropdownToggle caret={false}>
                          <div id="avatar-element" />
                        </DropdownToggle>
                        <DropdownMenu className="header-dropdown">
                          {getOptions()}
                        </DropdownMenu>
                      </Dropdown>
                    </NavLink>
                  </NavItem>
                </>
              )}
              {!isLoggedIn && (
                <>
                  <NavItem>
                    <NavLink tag="div" className={isPathActive("/login") ? "active" : ""}>
                      <Link to="/login">
                        <RiLoginCircleLine />
                        Login
                      </Link>
                    </NavLink>
                  </NavItem>
                </>
              )}
            </ul>
          )}
        </div>
      </nav>
      {isAddNewOpen && <AddNewRecord close={() => setAddNewOpen(false)} />}
      {isPaymentModalOpen && (
        <PaymentModal
          onClose={() => setPaymentModalOpen(false)}
          onSuccess={handlePaymentSuccess}
        />
      )}
      {successMessage && (
        <div className="payment-success-banner">
          <div className="success-content">
            ✓ {successMessage}
          </div>
        </div>
      )}
      {props.children}
    </div>
  );
};

export default Header;
